import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';

import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormlySelectOptionsPipe } from '@ngx-formly/core/select';

import { ContentContainerComponent } from '@shared/preloader';
import { GenericButtonComponent } from '@shared/ui';
import { MultipleSelectStore, DEFAULT_VISIBLE_OPTIONS_COUNT } from './multiple-select.store';
import { ArrayUtil } from '@core/utils';

@Component({
  selector: 'fc-multiple-select',
  templateUrl: './multiple-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: '../generic-select.component.scss',
  providers: [MultipleSelectStore, FormlySelectOptionsPipe],
  imports: [
    A11yModule,
    ContentContainerComponent,
    FormsModule,
    GenericButtonComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatTooltipModule,
    NgIf,
    ReactiveFormsModule,
  ],
})
export class MultipleSelectComponent implements OnInit {
  readonly field = input<FormlyFieldConfig>();

  protected readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(MultipleSelectStore);
  protected readonly formlySelectOptionsPipe = inject(FormlySelectOptionsPipe);
  protected readonly DEFAULT_VISIBLE_OPTIONS_COUNT = DEFAULT_VISIBLE_OPTIONS_COUNT;
  constructor() {
    effect(() => {
      const internalValues = this.store.selectedOptions().map(o => o.value);
      const externalValues = Array.isArray(this.field()?.formControl?.value) ? this.field()?.formControl?.value : [];
      const equals = ArrayUtil.isShallowEqual(internalValues, externalValues);
      if (equals) return;

      if (internalValues.length) {
        this.field()?.formControl?.setValue(internalValues, { emitEvent: true });
      } else {
        this.field()?.formControl?.reset([], { emitEvent: true });
      }
    });
  }

  ngOnInit(): void {
    const f = this.field();

    this.initializeOptions(f?.formControl?.value);

    if (typeof f?.props?.['visibleOptionsCount'] === 'number') {
      this.store.setInitialVisibleOptionsCount(f?.props?.['visibleOptionsCount']);
    }

    f
      ?.formControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(options => {
        const internalValues = this.store.selectedOptions().map(o => o.value);
        const externalValues = Array.isArray(options) ? options : [];
        const equals = ArrayUtil.isShallowEqual(internalValues, externalValues);
        if (equals) return;

        if (externalValues.length) {
          this.initializeOptions(f?.formControl?.value);
        } else {
          this.store.toggleAllOptions(false);
        }
      });
  }

  private initializeOptions(options: any[]) {
    this.store.setOptions(
      this.formlySelectOptionsPipe.transform(this.field()?.props?.options ?? [], this.field()),
      options
    );
  }
}
